/**
 * Consent-aware Google Ads conversion bridge for Today Merch lead events.
 *
 * Reads its configuration from:
 * <script type="application/json" data-today-merch-google-ads>
 *   {"enabled":true,"conversionId":"AW-123456789","quoteSubmittedLabel":"abcDEF_123",
 *    "projectSubmittedLabel":"ghiJKL_456","cardPurchaseLabel":"mnoPQR_789"}
 * </script>
 * The cart journey labels are optional: without `projectSubmittedLabel`, project
 * submissions are ignored; without `cardPurchaseLabel`, card authorizations are.
 */
(() => {
  'use strict';

  const CONFIG_SELECTOR = 'script[data-today-merch-google-ads]';
  const CONSENT_EVENT = 'todaymerch:google-consent-updated';
  const LEAD_EVENT = 'todaymerch:lead-event';
  const TRUSTED_SOURCE = 'today_merch_lead_attribution';
  const SENT_STORAGE_KEY = 'todaymerch_google_ads_conversions_v1';
  const MAX_SENT_KEYS = 100;
  const GLOBAL_STATE_KEY = '__todayMerchGoogleAdsBridge';
  const DEFAULT_WEBSITE_CALL_NUMBER = '01 85 13 44 26';
  const BUSINESS_PHONE_E164 = '+33185134426';
  const PHONE_TEXT_PATTERN = /(?:\+33|0033|33|0)[\s().-]*1(?:[\s().-]*\d{2}){4}/u;
  const PHONE_VALUE_PATTERN = /^\+?[\d\s().-]+$/u;
  const LABEL_PATTERN = /^[A-Za-z0-9_-]{4,128}$/u;
  const MAX_CONVERSION_VALUE = 10000000;

  function safeStorage() {
    try {
      const storage = window.sessionStorage;
      if (!storage) return null;
      storage.getItem(SENT_STORAGE_KEY);
      return storage;
    } catch {
      return null;
    }
  }

  function readSentKeys() {
    const storage = safeStorage();
    if (!storage) return new Set();

    try {
      const serialized = storage.getItem(SENT_STORAGE_KEY);
      if (!serialized) return new Set();
      const keys = JSON.parse(serialized);
      if (!Array.isArray(keys)) throw new Error('Invalid sent conversion store');
      return new Set(keys.filter((key) => typeof key === 'string').slice(-MAX_SENT_KEYS));
    } catch {
      try {
        storage.removeItem(SENT_STORAGE_KEY);
      } catch {}
      return new Set();
    }
  }

  function persistSentKeys(keys) {
    const storage = safeStorage();
    if (!storage) return;

    try {
      storage.setItem(
        SENT_STORAGE_KEY,
        JSON.stringify(Array.from(keys).slice(-MAX_SENT_KEYS)),
      );
    } catch {}
  }

  function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function normalizePhone(value) {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (!PHONE_VALUE_PATTERN.test(trimmed)) return '';

    const hasInternationalPrefix = trimmed.startsWith('+');
    const digits = trimmed.replace(/[^\d]/gu, '');
    if (digits.length < 8) return '';

    let candidate = '';
    if (hasInternationalPrefix) candidate = `+${digits}`;
    else if (digits.startsWith('0033') && digits.length > 4) candidate = `+33${digits.slice(4)}`;
    else if (digits.startsWith('33') && digits.length > 2) candidate = `+${digits}`;
    else if (digits.startsWith('0') && digits.length === 10) candidate = `+33${digits.slice(1)}`;
    else candidate = `+${digits}`;

    return /^\+[1-9]\d{7,14}$/u.test(candidate) ? candidate : '';
  }

  function findBusinessPhoneText(value) {
    if (typeof value !== 'string') return null;
    const match = value.match(PHONE_TEXT_PATTERN);
    if (!match || normalizePhone(match[0]) !== BUSINESS_PHONE_E164) return null;
    return match[0];
  }

  function collectPhoneTextNodes(root) {
    const nodes = [];

    function visit(node) {
      if (!node) return;
      if (node.nodeType === 3) {
        if (/\d/u.test(node.nodeValue || '')) nodes.push(node);
        return;
      }
      for (const child of Array.from(node.childNodes || [])) visit(child);
    }

    visit(root);
    return nodes;
  }

  function parseConfig() {
    const nodes = Array.from(document.querySelectorAll(CONFIG_SELECTOR));
    if (nodes.length !== 1) return null;

    let parsed;
    try {
      parsed = JSON.parse(nodes[0].textContent || '{}');
    } catch {
      return null;
    }

    if (!isPlainObject(parsed)) return null;

    const conversionId = typeof parsed.conversionId === 'string'
      ? parsed.conversionId.trim()
      : '';
    const quoteSubmittedLabel = typeof parsed.quoteSubmittedLabel === 'string'
      ? parsed.quoteSubmittedLabel.trim()
      : '';
    const websiteCallLabel = typeof parsed.websiteCallLabel === 'string'
      ? parsed.websiteCallLabel.trim()
      : '';
    const websiteCallNumber = typeof parsed.websiteCallNumber === 'string'
      && parsed.websiteCallNumber.trim()
      ? parsed.websiteCallNumber.trim()
      : DEFAULT_WEBSITE_CALL_NUMBER;
    const projectSubmittedLabel = typeof parsed.projectSubmittedLabel === 'string'
      ? parsed.projectSubmittedLabel.trim()
      : '';
    const cardPurchaseLabel = typeof parsed.cardPurchaseLabel === 'string'
      ? parsed.cardPurchaseLabel.trim()
      : '';
    const orderPurchaseLabel = typeof parsed.orderPurchaseLabel === 'string'
      ? parsed.orderPurchaseLabel.trim()
      : '';

    if (parsed.enabled !== true) return null;
    if (!/^AW-[0-9]{6,20}$/u.test(conversionId)) return null;
    if (!LABEL_PATTERN.test(quoteSubmittedLabel)) return null;

    const websiteCall = parseWebsiteCallConfig(
      conversionId,
      quoteSubmittedLabel,
      websiteCallLabel,
      websiteCallNumber,
    );
    // An invalid or reused label disables only that cart journey destination.
    const journeySendTo = (label, otherLabels) => (LABEL_PATTERN.test(label) && !otherLabels.includes(label)
      ? `${conversionId}/${label}`
      : null);
    const projectSubmittedSendTo = journeySendTo(
      projectSubmittedLabel,
      [quoteSubmittedLabel, websiteCallLabel, cardPurchaseLabel],
    );
    const cardPurchaseSendTo = journeySendTo(
      cardPurchaseLabel,
      [quoteSubmittedLabel, websiteCallLabel, projectSubmittedLabel],
    );
    const orderPurchaseSendTo = journeySendTo(orderPurchaseLabel,
      [quoteSubmittedLabel, websiteCallLabel, projectSubmittedLabel, cardPurchaseLabel]);

    return Object.freeze({
      cardPurchaseSendTo,
      orderPurchaseSendTo,
      conversionId,
      managedByGtm: parsed.managedByGtm === true,
      projectSubmittedSendTo,
      quoteSubmittedLabel,
      quoteSubmittedSendTo: `${conversionId}/${quoteSubmittedLabel}`,
      websiteCall,
    });
  }

  function parseWebsiteCallConfig(conversionId, quoteSubmittedLabel, label, number) {
    if (!label) return null;
    if (!LABEL_PATTERN.test(label)) return null;
    if (label === quoteSubmittedLabel) return null;
    if (normalizePhone(number) !== BUSINESS_PHONE_E164) return null;

    return Object.freeze({
      label,
      number,
      sendTo: `${conversionId}/${label}`,
    });
  }

  function hasAdsConsent(detail) {
    return ['ad_storage', 'ad_user_data', 'ad_personalization'].every(
      (key) => detail?.[key] === 'granted',
    );
  }

  function isLeadId(value) {
    return typeof value === 'string' && /^tm_lead_[0-9a-z-]{8,80}$/iu.test(value);
  }

  function isTrustedQuoteSubmission(payload) {
    return isPlainObject(payload)
      && payload.event === 'quote_submitted'
      && payload.conversion_role === 'primary'
      && payload.event_source === TRUSTED_SOURCE
      && isLeadId(payload.lead_id);
  }

  function isTrustedJourneyConversion(payload, eventName) {
    return isPlainObject(payload)
      && payload.event === eventName
      && payload.conversion_role === 'primary'
      && payload.event_source === TRUSTED_SOURCE
      && (eventName === 'project_order_confirmed' ? /^tm_order_[a-f0-9]{32}$/.test(payload.lead_id || '') : isLeadId(payload.lead_id));
  }

  function conversionValue(payload) {
    const { value } = payload;
    if (payload.currency !== 'EUR' || typeof value !== 'number' || !Number.isFinite(value)
      || value < 0 || value > MAX_CONVERSION_VALUE) return {};
    return { value: Math.round(value * 100) / 100, currency: 'EUR' };
  }

  // The cart journey keeps its project identifier in the query string: never send it.
  function pageLocationWithoutQuery() {
    try {
      const url = new URL(window.location.href);
      return { page_location: `${url.origin}${url.pathname}` };
    } catch {
      return {};
    }
  }

  function createBridge(config) {
    const pending = [];
    const sentKeys = new Set();
    const restoredPhoneAnchors = new Map();
    let consentGranted = false;
    let configured = false;
    let websiteCallConfigured = false;
    let consentGeneration = 0;
    let tagRequested = false;

    function gtag() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    }

    function ensureGtag() {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') window.gtag = gtag;
      return typeof window.gtag === 'function';
    }

    function requestGoogleTag() {
      if (tagRequested) return false;

      const existing = document.querySelector(
        `script[data-today-merch-google-ads-loader="${config.conversionId}"]`,
      );
      const existingGoogleTag = Array.from(document.querySelectorAll('script[src]')).some((node) => {
        try {
          const url = new URL(node.src, document.baseURI);
          const layer = url.searchParams.get('l');
          return url.protocol === 'https:'
            && ['www.googletagmanager.com', 'googletagmanager.com'].includes(url.hostname)
            && url.pathname === '/gtag/js'
            && (!layer || layer === 'dataLayer');
        } catch {
          return false;
        }
      });
      if (existing || existingGoogleTag) {
        tagRequested = true;
        return false;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.conversionId)}`;
      script.setAttribute('data-today-merch-google-ads-loader', config.conversionId);
      const target = document.head || document.documentElement;
      target.appendChild(script);
      tagRequested = true;
      return true;
    }

    function configureTag() {
      if (configured || !consentGranted || !ensureGtag()) return;
      if (!config.managedByGtm && requestGoogleTag()) window.gtag('js', new Date());
      window.gtag('set', 'allow_ad_personalization_signals', false);
      if (config.managedByGtm) {
        // The consent-gated GTM Google tag owns the base Ads destination.
        window.dataLayer.push({ event: 'todaymerch_google_ads_init' });
      } else {
        window.gtag('config', config.conversionId, {
          allow_ad_personalization_signals: false,
        });
      }
      configured = true;
    }

    function configureWebsiteCall() {
      if (websiteCallConfigured || !config.websiteCall || !consentGranted || !ensureGtag()) return;
      configureTag();
      if (!configured || typeof window.gtag !== 'function') return;

      const generation = consentGeneration;
      try {
        window.gtag('config', config.websiteCall.sendTo, {
          allow_ad_personalization_signals: false,
          phone_conversion_callback(formattedNumber, mobileNumber) {
            if (!consentGranted || generation !== consentGeneration) return;
            replaceWebsiteCallNumbers(formattedNumber, mobileNumber);
          },
          phone_conversion_number: config.websiteCall.number,
          phone_conversion_options: { cache: false },
        });
      } catch {
        return;
      }
      websiteCallConfigured = true;
    }

    function recordOriginalAnchor(anchor) {
      if (restoredPhoneAnchors.has(anchor)) return;
      restoredPhoneAnchors.set(anchor, {
        href: anchor.getAttribute('href'),
        textNodes: collectPhoneTextNodes(anchor).map((node) => ({
          node,
          value: node.nodeValue,
        })),
      });
    }

    function restoreWebsiteCallNumbers() {
      for (const [anchor, original] of restoredPhoneAnchors) {
        try {
          if (original.href === null) anchor.removeAttribute('href');
          else anchor.setAttribute('href', original.href);
          for (const entry of original.textNodes) entry.node.nodeValue = entry.value;
        } catch {}
      }
      restoredPhoneAnchors.clear();
    }

    function replaceWebsiteCallNumbers(formattedNumber, mobileNumber) {
      if (typeof formattedNumber !== 'string' || !formattedNumber.trim()) return;
      const replacementHrefNumber = normalizePhone(mobileNumber);
      const replacementTextNumber = normalizePhone(formattedNumber);
      if (!replacementHrefNumber || replacementTextNumber !== replacementHrefNumber) return;

      const anchors = Array.from(document.querySelectorAll('a[href^="tel:"]'));
      for (const anchor of anchors) {
        const href = anchor.getAttribute('href') || '';
        if (normalizePhone(href.replace(/^tel:/iu, '')) !== BUSINESS_PHONE_E164) continue;

        const matchingTextNodes = collectPhoneTextNodes(anchor)
          .map((node) => ({ node, source: findBusinessPhoneText(node.nodeValue) }))
          .filter((entry) => entry.source);
        if (matchingTextNodes.length === 0) continue;

        recordOriginalAnchor(anchor);
        anchor.setAttribute('href', `tel:${replacementHrefNumber}`);
        for (const { node, source } of matchingTextNodes) {
          node.nodeValue = node.nodeValue.replace(source, formattedNumber);
        }
      }
    }

    function conversionTarget(payload) {
      // Quotes never carry a value; the pre-tax amount belongs to cart journey outcomes only.
      if (isTrustedQuoteSubmission(payload)) return { sendTo: config.quoteSubmittedSendTo, parameters: {} };
      const sendTo = isTrustedJourneyConversion(payload, 'project_submitted') ? config.projectSubmittedSendTo
        : isTrustedJourneyConversion(payload, 'project_order_confirmed') ? config.orderPurchaseSendTo
        : isTrustedJourneyConversion(payload, 'project_card_authorized') ? config.cardPurchaseSendTo
          : null;
      if (!sendTo) return null;
      return { sendTo, parameters: { ...conversionValue(payload), ...pageLocationWithoutQuery() } };
    }

    function sendLeadConversion(payload, options = {}) {
      const target = conversionTarget(payload);
      if (!target) return false;
      const key = `${payload.event}:${payload.lead_id}`;
      if (sentKeys.has(key)) return false;

      if (!consentGranted) {
        if (options.queueWhenDenied === true) pending.push({ ...payload });
        return false;
      }

      configureTag();
      if (!configured || typeof window.gtag !== 'function') return false;

      try {
        window.gtag('event', 'conversion', {
          send_to: target.sendTo,
          transaction_id: payload.lead_id,
          ...target.parameters,
        });
      } catch {
        return false;
      }

      sentKeys.add(key);
      persistSentKeys(sentKeys);
      return true;
    }

    function flushPending() {
      for (const payload of pending.splice(0)) sendLeadConversion(payload);
    }

    function handleConsent(event) {
      consentGranted = hasAdsConsent(event.detail);
      if (!consentGranted) {
        consentGeneration += 1;
        websiteCallConfigured = false;
        restoreWebsiteCallNumbers();
        pending.splice(0);
        try {
          window.sessionStorage?.removeItem(SENT_STORAGE_KEY);
        } catch {}
        return;
      }
      for (const key of readSentKeys()) sentKeys.add(key);
      configureTag();
      configureWebsiteCall();
      flushPending();
    }

    function handleLeadEvent(event) {
      sendLeadConversion(event.detail);
    }

    function consumeKnownDataLayerEvents() {
      if (!Array.isArray(window.dataLayer)) return;
      for (const item of window.dataLayer) {
        if (isPlainObject(item)) sendLeadConversion(item, { queueWhenDenied: true });
      }
    }

    function initialize() {
      document.addEventListener(CONSENT_EVENT, handleConsent);
      document.addEventListener(LEAD_EVENT, handleLeadEvent);

      const initialConsent = window.todayMerchGoogleConsent;
      const recordedChoiceReady = window.TodayMerchPrivacyState?.ready === true
        && window.TodayMerchPrivacyState?.recordedChoice === true;
      // The default denial is not a resolved refusal: wait without touching storage.
      // A recorded choice or a later CMP event still applies the normal revocation path.
      if (initialConsent && (hasAdsConsent(initialConsent) || recordedChoiceReady)) {
        handleConsent({ detail: initialConsent });
      }
      consumeKnownDataLayerEvents();
    }

    return Object.freeze({
      initialize,
      sentKeys,
    });
  }

  function initialize() {
    if (window.Shopify?.designMode) return;
    if (window[GLOBAL_STATE_KEY]) return;

    const config = parseConfig();
    if (!config) return;

    const bridge = createBridge(config);
    window[GLOBAL_STATE_KEY] = bridge;
    bridge.initialize();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
